// src/api/rostersApi.js
import { api } from "./client";

/**
 * Small helpers that let us try multiple endpoint shapes safely.
 */
async function tryGet(url) {
  try {
    const res = await api.get(url);
    if (res?.status >= 200 && res?.status < 300) {
      return { ok: true, status: res.status, data: res.data };
    }
    return { ok: false, status: res?.status ?? 0, data: res?.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status ?? 0, error: e?.message, data: e?.response?.data };
  }
}

async function tryPost(url, body) {
  try {
    const res = await api.post(url, body);
    if (res?.status >= 200 && res?.status < 300) {
      return { ok: true, status: res.status, data: res.data };
    }
    return { ok: false, status: res?.status ?? 0, data: res?.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status ?? 0, error: e?.message, data: e?.response?.data };
  }
}

async function tryPut(url, body) {
  try {
    const res = await api.put(url, body);
    if (res?.status >= 200 && res?.status < 300) {
      return { ok: true, status: res.status, data: res.data };
    }
    return { ok: false, status: res?.status ?? 0, data: res?.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status ?? 0, error: e?.message, data: e?.response?.data };
  }
}

async function tryPatch(url, body) {
  try {
    const res = await api.patch(url, body);
    if (res?.status >= 200 && res?.status < 300) {
      return { ok: true, status: res.status, data: res.data };
    }
    return { ok: false, status: res?.status ?? 0, data: res?.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status ?? 0, error: e?.message, data: e?.response?.data };
  }
}

async function tryDelete(url) {
  try {
    // Prefer the canonical method name we control (`del`)
    const res = await api.del(url);
    if (res?.status >= 200 && res?.status < 300) {
      return { ok: true, status: res.status, data: res.data };
    }

    // Log unsuccessful attempts for debugging
    if (typeof window !== "undefined" && window?.console?.debug) {
      window.console.debug("[delete]", url, "→", res?.status, res?.data);
    }

    return { ok: false, status: res?.status ?? 0, data: res?.data };
  } catch (e) {
    // If some older code path exposes api.delete, try it as a fallback without throwing
    try {
      if (typeof api["delete"] === "function") {
        const res2 = await api["delete"](url);
        if (res2?.status >= 200 && res2?.status < 300) {
          return { ok: true, status: res2.status, data: res2.data };
        }

        if (typeof window !== "undefined" && window?.console?.debug) {
          window.console.debug("[delete-fallback]", url, "→", res2?.status, res2?.data);
        }

        return { ok: false, status: res2?.status ?? 0, data: res2?.data };
      }
    } catch (_) {}

    // Safe final log
    if (typeof window !== "undefined" && window?.console?.debug) {
      window.console.debug("[delete-error]", url, e);
    }

    return {
      ok: false,
      status: e?.response?.status ?? 0,
      error: e?.message,
      data: e?.response?.data,
    };
  }
}


// Small helper to try a list of URLs in order
async function tryDeleteMany(urls) {
  for (const u of urls) {
    const r = await tryDelete(u);
    if (r.ok) return r;
  }
  return { ok: false };
}
async function tryGetMany(urls) {
  for (const u of urls) {
    const r = await tryGet(u);
    if (r.ok) return r;
  }
  return { ok: false };
}
async function tryPostMany(urls, body) {
  for (const u of urls) {
    const r = await tryPost(u, body);
    if (r.ok) return r;
  }
  return { ok: false };
}
async function tryPutMany(urls, body) {
  for (const u of urls) {
    const r = await tryPut(u, body);
    if (r.ok) return r;
  }
  return { ok: false };
}
async function tryPatchMany(urls, body) {
  for (const u of urls) {
    const r = await tryPatch(u, body);
    if (r.ok) return r;
  }
  return { ok: false };
}

export const rostersApi = {
  // List rosters
  async list() {
    // Prefer trailing-slash DRF routes first
    const listCandidates = [
      "/api/rosters/",
      "/api/rosters",
      "/api/groups/",
      "/api/groups",
    ];
    const rep = await tryGetMany(listCandidates);
    if (rep.ok) {
      return {
        ok: true,
        rosters: Array.isArray(rep.data) ? rep.data : rep.data?.results ?? rep.data,
      };
    }
    return { ok: false, error: "Unable to load rosters." };
  },

  // Delete a roster
  async remove(id) {
    // Prefer groups (your backend calls them Student Groups) and DRF detail with trailing slash
    const delCandidates = [
      `/api/groups/${id}/`,
      `/api/groups/${id}`,
      `/api/rosters/${id}/`,
      `/api/rosters/${id}`,
      `/api/student_groups/${id}/`,
      `/api/student_groups/${id}`,
      `/api/student-groups/${id}/`,
      `/api/student-groups/${id}`,
    ];
    const rep = await tryDeleteMany(delCandidates);
    if (rep.ok) return { ok: true };
    return { ok: false, error: "Unable to delete roster." };
  },

  // Create with built-in flexibility (this likely already exists in your file, keep it!)
  async create({ name, description, studentIds }) {
    // 1) One-shot with student_ids
    let rep = await tryPostMany(
      ["/api/rosters/", "/api/rosters"],
      { name, description, student_ids: studentIds }
    );
    if (rep.ok) return { ok: true, status: rep.status, roster: rep.data };

    // alt body shape
    rep = await tryPostMany(
      ["/api/rosters/", "/api/rosters"],
      { name, description, studentIds }
    );
    if (rep.ok) return { ok: true, status: rep.status, roster: rep.data };

    // 2) two-step: create then attach
    rep = await tryPostMany(
      ["/api/rosters/", "/api/rosters"],
      { name, description }
    );
    if (rep.ok) {
      const roster = rep.data;
      const rid = roster?.id ?? roster?.roster_id ?? roster?.uuid;
      if (rid && Array.isArray(studentIds) && studentIds.length) {
        const attachUrls = [
          `/api/rosters/${rid}/students/`,
          `/api/rosters/${rid}/students`,
          `/api/rosters/${rid}/members/`,
          `/api/rosters/${rid}/members`,
          `/api/groups/${rid}/students/`,
          `/api/groups/${rid}/students`,
          `/api/groups/${rid}/members/`,
          `/api/groups/${rid}/members`,
        ];
        const attach =
          (await tryPostMany(attachUrls, { student_ids: studentIds })) ||
          { ok: false };
        if (attach.ok) return { ok: true, status: 200, roster };
        return { ok: false, status: 400, roster, error: "Created roster but could not attach students." };
      }
      return { ok: true, status: rep.status, roster };
    }

    // 3) groups alias
    rep = await tryPostMany(
      ["/api/groups/", "/api/groups"],
      { name, description, student_ids: studentIds }
    );
    if (rep.ok) return { ok: true, status: rep.status, roster: rep.data };

    // 4) convenience
    rep = await tryPostMany(
      ["/api/rosters/create_with_students/", "/api/rosters/create_with_students"],
      { name, description, student_ids: studentIds }
    );
    if (rep.ok) return { ok: true, status: rep.status, roster: rep.data };

    return { ok: false, status: 400, error: "Unable to create roster with provided endpoints." };
  },

  // Get a single roster by id
  async getRoster(id) {
    const rep = await tryGetMany([
      `/api/rosters/${id}/`,
      `/api/rosters/${id}`,
      `/api/groups/${id}/`,
      `/api/groups/${id}`,
    ]);
    if (rep.ok) return { ok: true, roster: rep.data };
    return { ok: false, error: "Unable to load roster." };
  },

  // Update roster name/description and student membership
  async update({ id, name, description, studentIds }) {
    // 1) Try PATCH/PUT to /api/rosters/:id with fields
    for (const updater of [tryPatchMany, tryPutMany]) {
      let u1 = await updater(
        [`/api/rosters/${id}/`, `/api/rosters/${id}`],
        { name, description, student_ids: studentIds }
      );
      if (u1.ok) return { ok: true, roster: u1.data };

      u1 = await updater(
        [`/api/rosters/${id}/`, `/api/rosters/${id}`],
        { name, description, studentIds }
      );
      if (u1.ok) return { ok: true, roster: u1.data };
    }

    // 2) Try PATCH/PUT name/description first, then separate membership endpoints
    for (const updater of [tryPatchMany, tryPutMany]) {
      const meta = await updater(
        [`/api/rosters/${id}/`, `/api/rosters/${id}`],
        { name, description }
      );
      if (meta.ok) {
        if (Array.isArray(studentIds)) {
          const membershipUrls = [
            `/api/rosters/${id}/students/`,
            `/api/rosters/${id}/students`,
            `/api/rosters/${id}/members/`,
            `/api/rosters/${id}/members`,
            `/api/groups/${id}/students/`,
            `/api/groups/${id}/students`,
            `/api/groups/${id}/members/`,
            `/api/groups/${id}/members`,
          ];
          // Prefer idempotent replace if backend supports it
          let attached =
            (await tryPutMany(membershipUrls, { student_ids: studentIds })).ok ||
            (await tryPostMany(membershipUrls, { student_ids: studentIds })).ok ||
            (await tryPatchMany(membershipUrls, { student_ids: studentIds })).ok;

          return attached ? { ok: true } : { ok: false, error: "Updated metadata but could not update members." };
        }
        return { ok: true };
      }
    }

    // 3) Try groups alias in one-shot
    for (const updater of [tryPatchMany, tryPutMany]) {
      let g = await updater(
        [`/api/groups/${id}/`, `/api/groups/${id}`],
        { name, description, student_ids: studentIds }
      );
      if (g.ok) return { ok: true, roster: g.data };

      g = await updater(
        [`/api/groups/${id}/`, `/api/groups/${id}`],
        { name, description, studentIds }
      );
      if (g.ok) return { ok: true, roster: g.data };
    }

    return { ok: false, error: "Unable to update roster." };
  },
};
